import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Checkbox, Dialog, Tooltip, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { printHtmlTable } from "../../../utils/printTable";
import { parseBarcode, endNumberFromTicket } from "../../../utils/barcode";
import { COLORS } from "./constants";
import InventoryTabs from "./InventoryTabs";
import ScanHelpTooltip from "./ScanHelpTooltip";
import ConfirmActivateModal from "./ConfirmActivateModal";

interface SaleDayDetailProps {
  apiBaseURL: string;
  shopId: any;
  socket: any;
  currencySymbol: string;
  initialDate: string;
  onBackToCalendar: () => void;
  onConfirmClick?: () => void;
  onActivateClick?: () => void;
  onSettlementClick?: () => void;
}

interface SalesRow {
  inventoryId: number;
  gameNumber: string;
  packNumber: string;
  name: string;
  imageUrl: string;
  screenNumber: number;
  ticketPosition: number;
  slotNumber: number;
  startNumber: number;
  endNumber: number;
  packSize: number;
  ticketValue: string;
  ticketsSold: number;
  amount: number;
  isSoldOut: boolean;
  edited: boolean;
  // a pack returned while active - render as a closed (red) row, inputs disabled
  isReturned?: boolean;
  returnedDate?: string;
}

interface RowEdit {
  endNumber?: string;
  slotNumber?: string;
  isSoldOut?: boolean;
}

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatLong = (d: string) => {
  const [y, m, day] = d.split("-");
  return `${MONTHS_FULL[parseInt(m) - 1]}/${day}/${y}`;
};
const formatSlash = (d: string) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
};
const addDays = (d: string, n: number) => {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().split("T")[0];
};

// A returned pack can be restored for 3 days from its return date (UI gate; the
// backend is the authority). returnedDate is a "YYYY-MM-DD" string.
const withinRestoreWindow = (returnedDate?: string): boolean => {
  if (!returnedDate) return false;
  const diff = Math.floor((Date.now() - new Date(`${returnedDate}T00:00:00`).getTime()) / 86400000);
  return diff >= 0 && diff <= 3;
};

const SaleDayDetail: React.FC<SaleDayDetailProps> = ({
  apiBaseURL,
  shopId,
  socket,
  currencySymbol,
  initialDate,
  onBackToCalendar,
  onConfirmClick,
  onActivateClick,
  onSettlementClick,
}) => {
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const [date, setDate] = React.useState<string>(initialDate);
  const [rows, setRows] = React.useState<SalesRow[]>([]);
  const [meta, setMeta] = React.useState<any>({});
  const [edits, setEdits] = React.useState<Record<number, RowEdit>>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [refreshToken, setRefreshToken] = React.useState<number>(0);
  const refresh = () => setRefreshToken(Math.random());

  const [confirmActivateOpen, setConfirmActivateOpen] = React.useState<boolean>(false);
  const [endDayOpen, setEndDayOpen] = React.useState<boolean>(false);
  const [quickEndDayOpen, setQuickEndDayOpen] = React.useState<boolean>(false);
  const [restoreTarget, setRestoreTarget] = React.useState<SalesRow | null>(null); // returned row pending restore confirm
  const [restoring, setRestoring] = React.useState<boolean>(false);

  // quick entry bar (scan / game / pack -> locate the active row)
  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  // end number read off the scanned ticket, pre-filled into the located row
  const [entryEndNumber, setEntryEndNumber] = React.useState<string>("");
  // the 3-digit ticket number carried in the barcode; converted to an ending number
  // once the row is located, because that conversion needs the pack size
  const [scannedTicketNumber, setScannedTicketNumber] = React.useState<string>("");
  const [highlightId, setHighlightId] = React.useState<number | null>(null);
  const endRefs = React.useRef<Record<number, HTMLInputElement | null>>({});
  // the cursor starts in the scan field, here as well as on the other scan screens
  const scanRef = React.useRef<HTMLInputElement | null>(null);

  // ---- auto-save bookkeeping ----
  // A save refetches the day, and the refetch used to drop every buffered edit. With
  // auto-save that would wipe a number being typed in another row while an earlier
  // row's save was still in flight, so a save now clears only the rows it saved.
  const pendingClearRef = React.useRef<number[] | null>(null);
  const autoSaveRef = React.useRef<boolean>(false);
  const [autoSaved, setAutoSaved] = React.useState<boolean>(false);

  const canEdit = !!meta.canEdit;
  const isCurrent = !!meta.isCurrentBusinessDate;

  // ---- fetch the day ----
  React.useEffect(() => {
    setLoading(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/sales/day`, { userId: shopId, date })
      .then((res: any) => {
        setRows(res?.data?.rows ?? []);
        setMeta(res?.data ?? {});
        const justSaved = pendingClearRef.current;
        pendingClearRef.current = null;
        setEdits((prev) => {
          // a plain reload (date change, manual refresh) starts from a clean slate
          if (!justSaved) return {};
          const next = { ...prev };
          justSaved.forEach((id) => delete next[id]);
          return next;
        });
      })
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")))
      .finally(() => {
        setLoading(false);
        // the day's table has rendered by now, so the scan field can take the cursor
        setTimeout(() => scanRef.current?.focus(), 0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseURL, shopId, date, refreshToken]);

  // ---- socket acks for the save ----
  React.useEffect(() => {
    if (!socket) return;
    const onSaved = () => {
      setSaving(false);
      // an auto-save is confirmed by the inline indicator, not a toast on every field
      if (autoSaveRef.current) {
        autoSaveRef.current = false;
        setAutoSaved(true);
      } else {
        showSuccessMessage(t("salesSavedMsg", "Sales updated successfully"));
      }
      refresh();
    };
    const onSaveError = (response: any) => {
      setSaving(false);
      autoSaveRef.current = false;
      // a rejected auto-save keeps its edits buffered so SAVE can retry them
      pendingClearRef.current = null;
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    const onRestored = () => {
      setRestoring(false);
      setRestoreTarget(null);
      showSuccessMessage(t("restoreSuccessMsg", "Pack restored successfully"));
      refresh();
    };
    const onRestoreError = (response: any) => {
      setRestoring(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    socket.on("saveInventoryV2SalesResponse", onSaved);
    socket.on("saveInventoryV2SalesError", onSaveError);
    socket.on("restoreInventoryV2Response", onRestored);
    socket.on("restoreInventoryV2Error", onRestoreError);
    return () => {
      socket.off("saveInventoryV2SalesResponse", onSaved);
      socket.off("saveInventoryV2SalesError", onSaveError);
      socket.off("restoreInventoryV2Response", onRestored);
      socket.off("restoreInventoryV2Error", onRestoreError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const handleRestore = () => {
    if (!restoreTarget) return;
    if (!socket) return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    setRestoring(true);
    socket.emit("restoreInventoryV2", { shopId, id: restoreTarget.inventoryId });
  };

  // ---- per-row derived values (apply local edits for a live preview) ----
  const rowSoldOut = (r: SalesRow) => {
    const e = edits[r.inventoryId]?.isSoldOut;
    return e !== undefined ? e : r.isSoldOut;
  };
  const rowEndValue = (r: SalesRow): string => {
    const e = edits[r.inventoryId]?.endNumber;
    if (e !== undefined) return e;
    return String(r.endNumber);
  };
  const rowSlotValue = (r: SalesRow): string => {
    const e = edits[r.inventoryId]?.slotNumber;
    if (e !== undefined) return e;
    return String(r.slotNumber);
  };
  const rowSold = (r: SalesRow) => {
    const soldOut = rowSoldOut(r);
    const end = soldOut ? r.packSize : (parseInt(rowEndValue(r)) || 0);
    // mirrors computeSold on the server: End is the next unsold ticket, so a normal
    // day sells end - start, and a sold-out pack counts its last ticket as well
    return Math.max(0, (end - r.startNumber) + (soldOut ? 1 : 0));
  };
  const rowAmount = (r: SalesRow) => rowSold(r) * (parseFloat(r.ticketValue) || 0);
  const isDirty = (r: SalesRow) => edits[r.inventoryId] !== undefined;
  const isEditedRow = (r: SalesRow) => r.edited || isDirty(r);

  const setEdit = (inventoryId: number, field: keyof RowEdit, value: any) => {
    setAutoSaved(false);
    setEdits((prev) => ({ ...prev, [inventoryId]: { ...prev[inventoryId], [field]: value } }));
  };

  // Does this row's buffered edit actually differ from what the server holds? Typing a
  // value back to its original leaves an edit behind, and there is no point saving it.
  const rowHasRealChange = (r: SalesRow): boolean => {
    const e = edits[r.inventoryId];
    if (!e) return false;
    if (e.isSoldOut !== undefined && !!e.isSoldOut !== !!r.isSoldOut) return true;
    if (e.endNumber !== undefined && e.endNumber !== "" && parseInt(e.endNumber) !== r.endNumber) return true;
    if (e.slotNumber !== undefined && e.slotNumber !== "" && parseInt(e.slotNumber) !== r.slotNumber) return true;
    return false;
  };

  const money = (v: number) => `${currencySymbol}${Number.isInteger(v) ? v : v.toFixed(2)}`;

  // ---- totals (live) ----
  const totalTicketsSold = rows.reduce((s, r) => s + rowSold(r), 0);
  const totalAmount = rows.reduce((s, r) => s + rowAmount(r), 0);
  const gamesEntered = rows.filter((r) => isEditedRow(r)).length;

  // ---- save (shared by the SAVE button and auto-save) ----
  // `overrides` carries a value that has not reached `edits` state yet, so a control
  // which saves on change (the sold-out checkbox) can send what the user just picked.
  const saveRows = (ids: number[], auto: boolean, overrides?: Record<number, RowEdit>) => {
    if (!ids.length) return;
    if (!socket) return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));

    // Catch an ending number outside the pack before it reaches the server. End is the
    // number on the NEXT unsold ticket, so the last valid value is the pack size - a
    // finished pack is recorded with Sold Out, not by typing one past the last ticket.
    for (const id of ids) {
      const row = rows.find((r) => r.inventoryId === id);
      const edit = { ...(edits[id] || {}), ...((overrides && overrides[id]) || {}) };
      if (!row) continue;
      // marking sold out replaces the ending number with the pack size, so whatever
      // is typed in the box is irrelevant and must not block the tick
      const markingSoldOut = edit.isSoldOut !== undefined ? edit.isSoldOut : row.isSoldOut;
      if (markingSoldOut) continue;
      if (edit.endNumber === undefined || edit.endNumber === "") continue;
      const end = parseInt(edit.endNumber);
      if (Number.isNaN(end)) continue;
      if (end > row.packSize) {
        return showErrorMessage(
          t("endBeyondPack", `Game ${row.gameNumber}: the ending number cannot be more than the pack size (${row.packSize}). Tick Sold Out if the pack is finished.`),
        );
      }
      if (end < row.startNumber) {
        return showErrorMessage(
          t("endBelowStart", `Game ${row.gameNumber}: the ending number cannot be less than the starting number (${String(row.startNumber).padStart(3, "0")}).`),
        );
      }
    }
    const payloadRows = ids.map((inventoryId) => ({
      inventoryId,
      ...(edits[inventoryId] || {}),
      ...((overrides && overrides[inventoryId]) || {}),
    }));
    autoSaveRef.current = auto;
    pendingClearRef.current = ids;
    setSaving(true);
    socket.emit("saveInventoryV2Sales", { shopId, date, rows: payloadRows });
  };

  const handleSave = () => {
    const dirtyIds = Object.keys(edits).map((k) => parseInt(k));
    if (!dirtyIds.length) return showErrorMessage(t("nothingToSave", "There are no changes to save"));
    saveRows(dirtyIds, false);
  };

  // Auto-save: a field commits when it loses focus, so a half-typed number is never
  // validated and never runs the forward cascade. SAVE stays as the manual retry.
  const handleFieldBlur = (r: SalesRow) => {
    if (saving || !canEdit) return;
    if (!rowHasRealChange(r)) return;
    saveRows([r.inventoryId], true);
  };

  const handleSoldOutChange = (r: SalesRow, checked: boolean) => {
    setEdit(r.inventoryId, "isSoldOut", checked);
    // a checkbox has no meaningful blur, and ticking it is a complete action
    if (!saving && canEdit) saveRows([r.inventoryId], true, { [r.inventoryId]: { isSoldOut: checked } });
  };

  // ---- end day ----
  // `quick` closes the day on the numbers already shown, skipping the "every ticket
  // must be updated" gate. Untouched rows end where they started, so 0 sold.
  const handleEndDay = (quick: boolean = false) => {
    axios
      .post(`${apiBaseURL}/api/inventory-v2/sales/endDay`, { userId: shopId, date, quick })
      .then((res: any) => {
        showSuccessMessage(t("dayEndedMsg", "Day ended successfully"));
        setEndDayOpen(false);
        setQuickEndDayOpen(false);
        setDate(res?.data?.nextDate ?? date); // move to the new current business date
      })
      .catch((error: any) => {
        setEndDayOpen(false);
        setQuickEndDayOpen(false);
        showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong"));
      });
  };

  // ---- day navigation ----
  const goPreviousDay = () => {
    if (!meta.canGoPrevious) return;
    setDate((d) => addDays(d, -1));
  };
  // canGoNext comes from the server and is false on the current business date, so
  // this can never walk into the future
  const goNextDay = () => {
    if (!meta.canGoNext) return;
    setDate((d) => addDays(d, 1));
  };

  // ---- quick entry: locate an active ticket's row ----
  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length >= 11) {
      const scanned = parseBarcode(value);
      setGameNumber(scanned.gameNumber);
      setPackNumber(scanned.packNumber);
      // held until the row is found - turning it into an ending number needs that
      // row's pack size
      if (scanned.ticketNumber) setScannedTicketNumber(scanned.ticketNumber);
    }
  };
  const locateRow = (game: string, pack: string, end: string) => {
    const row = rows.find((r) => r.gameNumber === game && (!pack || r.packNumber === pack));
    if (!row) {
      return showErrorMessage(t("onlyActivatedInSales", "Only activated tickets can be recorded here"));
    }
    setHighlightId(row.inventoryId);
    // a typed ending number wins; otherwise derive it from the scanned ticket number
    if (!end && scannedTicketNumber) {
      end = endNumberFromTicket(scannedTicketNumber, row.packSize);
    }
    // an ending number entered or scanned up here lands in that row's End # box; it is
    // left for the user to confirm (it saves when the field loses focus)
    if (end) setEdit(row.inventoryId, "endNumber", end);
    const el = endRefs.current[row.inventoryId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
    }
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
    setEntryEndNumber("");
    setScannedTicketNumber("");
  };
  const submitLocate = () => {
    const game = (gameNumber || scanCode.substring(0, 4)).trim();
    const pack = (packNumber || scanCode.substring(4, 11)).trim();
    locateRow(game, pack, entryEndNumber.trim());
  };
  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    submitLocate();
  };

  // ---- print (the table only - see utils/printTable) ----
  const printTable = () => {
    const ok = printHtmlTable({
      title: `Sales Report - ${formatLong(date)}`,
      subtitle: `Sale Date: ${formatLong(date)} &nbsp;|&nbsp; Business Date: ${formatSlash(meta.currentBusinessDate)}`,
      head: ["Image", "Name", "Game #", "Pack #", "Pack Size #", "Slot #", "Start #", "End #", "Sold Out", "Tickets Sold", "Tickets Value", "Amount"],
      rows: rows.map((r) => [
        r.imageUrl ? `<img src="${r.imageUrl}" style="height:34px"/>` : "",
        r.name, r.gameNumber, r.packNumber, r.packSize, rowSlotValue(r), r.startNumber,
        rowSoldOut(r) ? "SOLD OUT" : rowEndValue(r), rowSoldOut(r) ? "Yes" : "No",
        rowSold(r), money(parseFloat(r.ticketValue) || 0), money(rowAmount(r)),
      ]),
      footer: { label: "Total", colSpan: 9, cells: [totalTicketsSold, "", money(totalAmount)] },
    });
    if (!ok) showErrorMessage(t("popupBlocked", "Please allow pop-ups to print"));
  };

  // ---- styles ----
  const thSx = { fontSize: "15px", fontWeight: 700, color: "#000", textAlign: "center", py: "10px", px: "6px", borderBottom: `2px solid ${COLORS.border}` } as const;
  const tdSx = { fontSize: "15px", color: "#222", textAlign: "center", py: "6px", px: "6px", borderBottom: `1px solid ${COLORS.border}` } as const;
  const cellInputSx = {
    width: "64px", height: "32px", borderRadius: "4px", border: `1px solid ${COLORS.border}`,
    textAlign: "center", fontSize: "15px", outline: "none", backgroundColor: "#fff",
    "&:disabled": { backgroundColor: "#f0f0f0", color: "#555" },
  } as const;

  // digits only - these inputs are text rather than number so they carry no spinner
  // arrows and, more importantly, cannot be changed by a stray mouse-wheel scroll
  const digitsOnly = (value: string) => value.replace(/[^0-9]/g, "");
  const topBtn = (bg: string) => ({
    backgroundColor: bg, color: "#fff", textTransform: "none", borderRadius: "6px", height: "40px",
    px: 2.5, fontSize: "16px", fontWeight: 600, "&:hover": { backgroundColor: bg, opacity: 0.9 },
    "&.Mui-disabled": { backgroundColor: "#bdbdbd", color: "#fff" },
  });

  return (
    <Box p={2}>
      {/* Header: back arrow + Sale Date + tabs */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mb={2} sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}>
        <Box display='flex' alignItems='center' gap='12px'>
          <Box component='span' onClick={onBackToCalendar} sx={{ cursor: "pointer", fontSize: "26px", lineHeight: 1, color: "#000" }} aria-label={t("back", "Back")}>
            ←
          </Box>
          <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#000" }}>
            {t("saleDateLabel", "Sale Date")} : {formatLong(date)}
          </Typography>
        </Box>
        <InventoryTabs active='sales' onConfirmClick={onConfirmClick} onActivateClick={onActivateClick} onSalesClick={onBackToCalendar} onSettlementClick={onSettlementClick} />
      </Box>

      {/* Control bar: Previous Day | entry panel | day-close controls (all vertically centered) */}
      <Box display='flex' alignItems='center' gap='14px' flexWrap='wrap' mb={1.5}>
        <Button onClick={goPreviousDay} disabled={!meta.canGoPrevious} disableElevation sx={topBtn("#000")}>
          ← {t("previousDay", "Previous Day")}
        </Button>

        <Box
          display='flex'
          alignItems='flex-end'
          justifyContent='space-evenly'
          gap='16px'
          flexWrap='wrap'
          sx={{ flex: 1, backgroundColor: COLORS.panelBg, border: "0.25px solid #434343", p: 1.5, borderRadius: "4px" }}
        >
          <Box sx={{ flex: "1.6 1 220px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("scanCodeLabel", "Scan Code")}</Typography>
            <Box display='flex' alignItems='center' gap='8px'>
              <Box component='input' ref={scanRef} autoFocus value={scanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)} onKeyDown={onEntryKeyDown} placeholder={t("scanCodePlaceholder", "Scan / enter barcode")} sx={{ flex: 1, minWidth: 0, height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff" }} />
              <ScanHelpTooltip />
            </Box>
          </Box>
          <Typography sx={{ fontSize: "16px", color: "#434343", pb: "8px" }}>{t("or", "OR")}</Typography>
          <Box sx={{ flex: "1 1 160px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("gameNumberLabel", "Game Number")}</Typography>
            <Box component='input' value={gameNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={{ width: "100%", height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" }} />
          </Box>
          <Box sx={{ flex: "1 1 160px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("packNumberLabel", "Pack Number")}</Typography>
            <Box component='input' value={packNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={{ width: "100%", height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" }} />
          </Box>
          <Box sx={{ flex: "1 1 120px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("endNumberLabel", "End Number")}</Typography>
            <Box component='input' inputMode='numeric' value={entryEndNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEntryEndNumber(digitsOnly(e.target.value))} onKeyDown={onEntryKeyDown} sx={{ width: "100%", height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" }} />
          </Box>
          <Button onClick={submitLocate} disableElevation sx={topBtn(COLORS.blue)}>
            {t("enterButton", "Enter")}
          </Button>
        </Box>

        {/* Day-close controls. A day other than the live business date can never be
            ended, so rather than showing two permanently dead buttons, Next Day takes
            their place; on the live day it sits underneath them. */}
        <Box display='flex' flexDirection='column' gap='8px'>
          {isCurrent ? (
            <>
              <Box display='flex' gap='10px'>
                <Button onClick={() => setEndDayOpen(true)} disabled={!meta.canEndDay} disableElevation sx={topBtn(COLORS.red)}>
                  {t("endDay", "End Day")}
                </Button>
                {/* closes the day on the numbers already entered, without needing
                    every row updated first */}
                <Button onClick={() => setQuickEndDayOpen(true)} disabled={!rows.length} disableElevation sx={topBtn(COLORS.navy)}>
                  {t("quickEndDay", "Quick End Day")}
                </Button>
              </Box>
              <Button onClick={goNextDay} disabled={!meta.canGoNext} disableElevation sx={{ ...topBtn("#000"), width: "100%" }}>
                {t("nextDay", "Next Day")} →
              </Button>
            </>
          ) : (
            <Button onClick={goNextDay} disabled={!meta.canGoNext} disableElevation sx={topBtn("#000")}>
              {t("nextDay", "Next Day")} →
            </Button>
          )}
        </Box>
      </Box>

      {/* Confirm & Activate button (right aligned) */}
      <Box display='flex' justifyContent='flex-end' mb={1.5}>
        <Button onClick={() => setConfirmActivateOpen(true)} disableElevation sx={topBtn(COLORS.navy)}>
          {t("confirmActivateButton", "Confirm & Activate")}
        </Button>
      </Box>

      {/* Status badges + print */}
      <Box display='flex' alignItems='center' gap='16px' mb={1}>
        <Box display='flex' alignItems='center' gap='6px'>
          <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>{t("gamesEntered", "Games Entered")}:</Typography>
          <Box sx={{ backgroundColor: COLORS.red, color: "#fff", borderRadius: "4px", px: "10px", py: "2px", fontWeight: 700, fontSize: "15px" }}>{gamesEntered}</Box>
        </Box>
        <Box display='flex' alignItems='center' gap='6px'>
          <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>{t("ticketsSoldBadge", "Tickets Sold")}:</Typography>
          <Box sx={{ backgroundColor: COLORS.green, color: "#000", borderRadius: "4px", px: "10px", py: "2px", fontWeight: 700, fontSize: "15px" }}>{money(totalAmount)}</Box>
        </Box>
        <Box flex={1} />
        <PrintIcon sx={{ cursor: "pointer", color: "#434343", fontSize: 28 }} onClick={printTable} />
      </Box>

      {/* Sales table */}
      <Box sx={{ width: "100%", overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: "4px", opacity: loading ? 0.6 : 1 }}>
        <Box component='table' sx={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
          <Box component='thead'>
            <Box component='tr' sx={{ backgroundColor: "#fff" }}>
              {["Image", "Name", "Game #", "Pack #", "Pack Size #", "Slot #", "Start #", "End #", "Sold Out?", "Tickets Sold", "Tickets Value", "Amount"].map((h) => (
                <Box component='th' key={h} sx={thSx}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component='tbody'>
            {rows.map((r, idx) => {
              const edited = isEditedRow(r);
              const soldOut = rowSoldOut(r);
              // A returned (closed) pack: red row, inputs disabled, sold-out checkbox
              // hidden, End frozen at its close number, inline Restore within 3 days.
              const returned = !!r.isReturned;
              const rowBg = returned
                ? COLORS.rowReturned
                : (edited ? COLORS.editedRow : (idx % 2 === 0 ? COLORS.rowDark : COLORS.rowLight));
              const rowContent = (
                <Box
                  component='tr'
                  key={r.inventoryId}
                  sx={{
                    backgroundColor: rowBg,
                    boxShadow: highlightId === r.inventoryId ? `inset 0 0 0 2px ${COLORS.blue}` : "none",
                  }}
                >
                  <Box component='td' sx={tdSx}>
                    {r.imageUrl ? <Box component='img' src={r.imageUrl} alt={r.name} sx={{ height: "34px", width: "auto" }} /> : "-"}
                  </Box>
                  <Box component='td' sx={tdSx}>{r.name}</Box>
                  <Box component='td' sx={tdSx}>{r.gameNumber}</Box>
                  <Box component='td' sx={tdSx}>{r.packNumber}</Box>
                  <Box component='td' sx={tdSx}>{r.packSize}</Box>
                  <Box component='td' sx={tdSx}>
                    {returned ? (
                      rowSlotValue(r)
                    ) : (
                      // shown as a disabled box rather than a bare label on past days,
                      // so the column reads the same whichever day is open
                      <Box
                        component='input'
                        inputMode='numeric'
                        disabled={!canEdit || !isCurrent}
                        value={rowSlotValue(r)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdit(r.inventoryId, "slotNumber", digitsOnly(e.target.value))}
                        onBlur={() => handleFieldBlur(r)}
                        sx={cellInputSx}
                      />
                    )}
                  </Box>
                  <Box component='td' sx={tdSx}>{String(r.startNumber).padStart(3, "0")}</Box>
                  <Box component='td' sx={{ ...tdSx, backgroundColor: soldOut ? COLORS.soldOutCell : "transparent" }}>
                    {returned ? (
                      <Box display='flex' alignItems='center' justifyContent='center' gap='6px'>
                        <span>{r.endNumber}</span>
                        {withinRestoreWindow(r.returnedDate) && (
                          <Button
                            onClick={() => setRestoreTarget(r)}
                            disableElevation
                            sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "4px", height: "26px", fontSize: "13px", fontWeight: 600, px: 1, minWidth: 0, "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
                          >
                            {t("restoreButton", "Restore")}
                          </Button>
                        )}
                      </Box>
                    ) : soldOut ? (
                      // an exhausted pack has no ending number to show
                      <Typography component='span' sx={{ fontSize: "14px", fontWeight: 700, color: "#000", letterSpacing: "0.5px" }}>
                        {t("soldOutLabel", "SOLD OUT")}
                      </Typography>
                    ) : (
                      <Box
                        component='input'
                        inputMode='numeric'
                        disabled={!canEdit}
                        ref={(el: HTMLInputElement | null) => { endRefs.current[r.inventoryId] = el; }}
                        value={rowEndValue(r)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdit(r.inventoryId, "endNumber", digitsOnly(e.target.value))}
                        onBlur={() => handleFieldBlur(r)}
                        sx={cellInputSx}
                      />
                    )}
                  </Box>
                  <Box component='td' sx={tdSx}>
                    {/* a returned pack is labelled instead of offering the checkbox */}
                    {returned ? (
                      <Typography component='span' sx={{ fontSize: "14px", fontWeight: 700, color: COLORS.red, letterSpacing: "0.5px" }}>
                        {t("returnedLabel", "RETURNED")}
                      </Typography>
                    ) : (
                      // A pack can only be closed on the live business date: doing it on
                      // a past day would invalidate every day that already rolled forward
                      // from it. Say so, rather than leaving a checkbox that does nothing.
                      <Tooltip
                        title={(!canEdit || !isCurrent) ? t("soldOutLiveDayOnly", "Sold out can only be set on the current business date") : ""}
                        placement='top'
                        arrow
                      >
                        <span>
                          <Checkbox
                            size='small'
                            checked={soldOut}
                            disabled={!canEdit || !isCurrent}
                            onChange={(e) => handleSoldOutChange(r, e.target.checked)}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  <Box component='td' sx={tdSx}>{rowSold(r)}</Box>
                  <Box component='td' sx={tdSx}>{money(parseFloat(r.ticketValue) || 0)}</Box>
                  <Box component='td' sx={tdSx}>{money(rowAmount(r))}</Box>
                </Box>
              );
              return returned ? (
                <Tooltip key={r.inventoryId} title={t("packReturnedTooltip", "Pack returned")} arrow placement='top'>
                  {rowContent}
                </Tooltip>
              ) : (
                rowContent
              );
            })}
            {!rows.length && (
              <Box component='tr'>
                <Box component='td' colSpan={12} sx={{ ...tdSx, py: 3, color: "#777" }}>
                  {t("noSalesRows", "No active tickets for this day")}
                </Box>
              </Box>
            )}
          </Box>
          {rows.length > 0 && (
            <Box component='tfoot'>
              <Box component='tr' sx={{ backgroundColor: COLORS.panelBg }}>
                <Box component='td' colSpan={9} sx={{ ...tdSx, textAlign: "right", fontWeight: 700, pr: 2 }}>
                  {t("totalTicketsSold", "Total Tickets Sold")}: {totalTicketsSold}
                </Box>
                <Box component='td' colSpan={3} sx={{ ...tdSx, textAlign: "left", fontWeight: 700 }}>
                  {t("totalSold", "Total Sold")}: {money(totalAmount)}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Business date + Save */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mt={1.5}>
        <Typography sx={{ fontSize: "15px", color: "#434343" }}>
          {t("businessDate", "Business Date")}: {formatSlash(meta.currentBusinessDate)}
        </Typography>
        <Box display='flex' alignItems='center' gap='12px'>
          <Typography sx={{ fontSize: "14px", color: saving ? "#434343" : "#2e7d32", minWidth: "60px" }}>
            {saving ? t("savingLabel", "Saving…") : (autoSaved ? t("savedLabel", "Saved") : "")}
          </Typography>
          <Button onClick={handleSave} disabled={saving || !canEdit} disableElevation sx={topBtn(COLORS.blue)}>
            {t("saveButton", "SAVE")}
          </Button>
        </Box>
      </Box>

      {/* General information help block (Figma) */}
      <Box sx={{ mt: 2, backgroundColor: COLORS.panelBg, border: `0.25px solid ${COLORS.border}`, borderRadius: "4px", p: 2 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, mb: 0.5 }}>{t("generalInformation", "General Information")}</Typography>
        <Box component='ul' sx={{ m: 0, pl: 3, fontSize: "13px", color: "#434343", lineHeight: 1.6 }}>
          <li>{t("saleHelp1", "Tickets can only be sold for the selected Business Date.")}</li>
          <li>{t("saleHelp2", "Records can be updated for up to 10 previous days from the Business Date.")}</li>
          <li>{t("saleHelp3", "Enter or scan the Game Number and Pack Number to load a ticket, then enter its Ending Number.")}</li>
          <li>{t("saleHelp4", "A row turns green once it is updated. End Day is enabled only after every ticket is updated.")}</li>
          <li>{t("saleHelp5", "Entries save automatically when you leave a field. Quick End Day closes the day on the numbers already entered.")}</li>
        </Box>
      </Box>

      <ConfirmActivateModal
        open={confirmActivateOpen}
        onClose={() => setConfirmActivateOpen(false)}
        socket={socket}
        apiBaseURL={apiBaseURL}
        shopId={shopId}
        onConfirmedActivated={refresh}
      />

      {/* End Day confirmation */}
      <Dialog open={endDayOpen} onClose={() => setEndDayOpen(false)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "420px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("endDayConfirmTitle", "End the day?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("endDayConfirmBody", "This will finalize all sales for this date. Tomorrow's starting numbers will be set from today's ending numbers. This cannot be undone.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setEndDayOpen(false)} disableElevation sx={topBtn(COLORS.grey)}>{t("cancel", "Cancel")}</Button>
            {/* passed explicitly: onClick={handleEndDay} would hand the click event in
                as `quick` and silently skip the per-row check */}
            <Button onClick={() => handleEndDay(false)} disableElevation sx={topBtn(COLORS.red)}>{t("endDay", "End Day")}</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Quick End Day confirmation - spells out that untouched rows count as no sales */}
      <Dialog open={quickEndDayOpen} onClose={() => setQuickEndDayOpen(false)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "440px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("quickEndDayConfirmTitle", "Quick end the day?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("quickEndDayConfirmBody", "This closes the day on the numbers already entered, without needing every ticket updated first. Any ticket you have not updated is recorded as no sales for the day, and tomorrow starts from the same number. This cannot be undone.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setQuickEndDayOpen(false)} disableElevation sx={topBtn(COLORS.grey)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={() => handleEndDay(true)} disableElevation sx={topBtn(COLORS.navy)}>{t("quickEndDay", "Quick End Day")}</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Restore (un-return) confirmation */}
      <Dialog open={!!restoreTarget} onClose={() => setRestoreTarget(null)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "420px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("restoreConfirmTitle", "Restore this pack?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("restoreConfirmBody", "This will restore the pack to its previous status. If it was active, it will be placed back on its slot.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setRestoreTarget(null)} disableElevation sx={topBtn(COLORS.grey)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleRestore} disabled={restoring} disableElevation sx={topBtn(COLORS.navy)}>{t("restoreButton", "Restore")}</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SaleDayDetail;
